import { Layout } from 'antd';
import React, { Component, Suspense, lazy } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import PrivateRoute from './components/Routing';
import NotFound from './components/NotFoundPage';
import Unauthorized from './pages/Unauthorized';
import ConditionalChatWidget from './components/ChatWidget/ConditionalChatWidget';
import TitleSection from './components/TitleSection';
import ActionFooter from './components/ActionFooter';

// ── Eager (public / auth / onboarding) ──────────────────────────────────
// These are the unauthenticated entry points + always-rendered chrome, so
// they stay in the main chunk for fast first paint. Splitting them out
// would only add a loading flash to the marketing / login surface.
import Login from './pages/dashboard/login';
import AssessmentCompleted from './pages/dashboard/assessmentCompleted';
import TrainerWelcome from './pages/dashboard/trainerWelcome';
import ComponentsShowcase from './pages/dev/Components';
import { LandingPage } from './presentational/LandingPage';
import ShortAssessment from './presentational/ShortAssessment';
import Verification from './pages/dashboard/verification';
import VerifyEmailMessage from './pages/dashboard/verifyEmailMessage';
import QuickSetup from './pages/dashboard/quickSetup';
import TrainerProfileSetup from './components/TrainerProfileSetup';
import ClientSetup from './components/ClientSetup';
import { SharedWorkout } from './components/SharableComponents/sharedWorkout';
import PublicChallengePage from './presentational/PublicChallengePage';
import { AchievementModal } from './components/AchievementModal/index';

// ── Lazy (authenticated dashboard) ──────────────────────────────────────
// The dashboard is the bulk of the app and is only reached after login, so
// each page is code-split into its own chunk (React.lazy). The Suspense
// boundary around the dashboard <Switch> (in render) shows a fallback while
// a page chunk loads; the layout chrome stays mounted. (Wave 1.5.)

// Wrap React.lazy so a failed chunk load triggers ONE full reload. After a
// deploy, a browser holding a stale index.html requests chunk hashes the
// server no longer has; the SPA fallback then serves index.html as JS, which
// throws "Unexpected token '<'" / ChunkLoadError when parsed. A reload pulls
// the fresh index.html + current chunks. Guarded via sessionStorage so a
// genuinely broken chunk can't loop; a successful load clears the guard.
const CHUNK_RELOAD_KEY = 'chunkReloadOnce';
const lazyWithReload = (importFn) =>
  lazy(() =>
    importFn()
      .then((mod) => {
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        } catch (e) {
          /* storage blocked */
        }
        return mod;
      })
      .catch((err) => {
        let reloadedOnce = false;
        try {
          reloadedOnce = !!sessionStorage.getItem(CHUNK_RELOAD_KEY);
        } catch (e) {
          /* storage blocked */
        }
        if (!reloadedOnce) {
          try {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
          } catch (e) {
            /* storage blocked */
          }
          window.location.reload();
          return new Promise(() => {}); // hold render during reload
        }
        throw err; // already reloaded once — surface to the ErrorBoundary
      }),
  );

const ChallengeLandingBuilder = lazyWithReload(() =>
  import('./presentational/ChallengeLandingBuilder')
);
const Home = lazyWithReload(() => import('./pages/dashboard/home'));
const Feedback = lazyWithReload(() => import('./pages/dashboard/feedback'));
const Profile = lazyWithReload(() => import('./pages/dashboard/profile'));
const MacroHistory = lazyWithReload(() => import('./pages/dashboard/macroHistory'));
const MacrosManual = lazyWithReload(() => import('./pages/dashboard/macrosManual'));
const MealPlan = lazyWithReload(() => import('./pages/dashboard/customMealPlan'));
const NutritionV2 = lazyWithReload(() => import('./pages/dashboard/nutritionV2'));
const SuggesticMealPlan = lazyWithReload(() => import('./pages/dashboard/suggesticMealPlan'));
const SuggesticShopping = lazyWithReload(() => import('./pages/dashboard/suggesticShopping'));
const Recipes = lazyWithReload(() => import('./pages/dashboard/recipes'));
const Settings = lazyWithReload(() => import('./pages/dashboard/settings'));
const Stats = lazyWithReload(() => import('./pages/dashboard/stat'));
const FoodLogHistory = lazyWithReload(() => import('./pages/dashboard/foodLogHistory'));
const FoodCompliance = lazyWithReload(() => import('./pages/dashboard/foodCompliance'));
const NutritionRestrictions = lazyWithReload(() => import('./pages/dashboard/nutritionRestrictions'));
const CustomShopping = lazyWithReload(() => import('./pages/dashboard/customShopping'));
const CustomFoodPreferences = lazyWithReload(() => import('./pages/dashboard/customFoodPreferences'));
const UserWorkouts = lazyWithReload(() => import('./pages/dashboard/userWorkouts'));
const UserWorkoutPrograms = lazyWithReload(() => import('./pages/dashboard/userWorkoutPrograms'));
const UserWorkoutPlans = lazyWithReload(() => import('./pages/dashboard/userWorkoutPlans'));
const ClientCheckInsHistory = lazyWithReload(() => import('./pages/dashboard/checkInsHistory'));
const YourWeek = lazyWithReload(() => import('./pages/dashboard/yourWeek'));
const ChallengeDetail = lazyWithReload(() => import('./pages/dashboard/challengeDetail'));
const ChallengesList = lazyWithReload(() => import('./pages/dashboard/challengesList'));
const AssessmentProfile = lazyWithReload(() => import('./pages/dashboard/assessmentProfile'));
const UserExerciseDetail = lazyWithReload(() => import('./pages/dashboard/userExerciseDetail'));
const Messages = lazyWithReload(() => import('./pages/dashboard/messages'));
// Named-export pages — map to { default } so React.lazy can consume them.
const UserExerciseHistory = lazy(() =>
  import('./pages/dashboard/UserExerciseHistory').then((m) => ({
    default: m.UserExerciseHistory,
  })),
);
const OneRepMaxTool = lazy(() =>
  import('./pages/dashboard/oneRepMaxTool').then((m) => ({
    default: m.OneRepMaxTool,
  })),
);
const CarbCyclingTool = lazy(() =>
  import('./pages/dashboard/carbCyclingTool').then((m) => ({
    default: m.CarbCyclingTool,
  })),
);
// Admin (trainer-only) pages — the heaviest set (meal-plan builder, charts,
// rich editors), never loaded for client accounts.
const AdminClients = lazyWithReload(() => import('./pages/adminDashboard/clients'));
const AdminTrainers = lazyWithReload(() => import('./pages/adminDashboard/trainers'));
const AdminDashboard = lazyWithReload(() => import('./pages/adminDashboard/trainerHome'));
const AdminFoodLog = lazyWithReload(() => import('./pages/adminDashboard/foodLog'));
const ClientFoodCompliance = lazyWithReload(() => import('./pages/adminDashboard/clientFoodCompliance'));
const AdminCreateMealPlan = lazyWithReload(() => import('./pages/adminDashboard/createMealplan'));
const AdminMealPlanLibrary = lazyWithReload(() => import('./pages/adminDashboard/mealPlanLibrary'));
const AdminMealPlanTemplate = lazyWithReload(() => import('./pages/adminDashboard/mealPlanTemplate'));
const AdminMealPlanTemplateV2 = lazyWithReload(() => import('./pages/adminDashboard/mealPlanTemplateV2'));
const AdminMealPlanClientV2 = lazyWithReload(() => import('./pages/adminDashboard/mealPlanClientV2'));
const AdminProgramClientV2 = lazyWithReload(() => import('./pages/adminDashboard/programClientV2'));
const AdminWorkoutLibrary = lazyWithReload(() => import('./pages/adminDashboard/workoutLibrary'));
const AdminClientProfile = lazyWithReload(() => import('./pages/adminDashboard/clientProfile'));
const AdminWorkoutProgramLibrary = lazyWithReload(() => import('./pages/adminDashboard/workoutProgramLibrary'));
const AdminProgramTemplateV2 = lazyWithReload(() => import('./pages/adminDashboard/programTemplateV2'));
const AdminWorkoutPlanLibrary = lazyWithReload(() => import('./pages/adminDashboard/workoutPlanLibrary'));
const AdminWorkoutPlanEditor = lazyWithReload(() => import('./pages/adminDashboard/workoutPlanEditor'));
const AdminAssessmentsPage = lazyWithReload(() => import('./pages/adminDashboard/assessments'));
const AdminChallenges = lazyWithReload(() => import('./pages/adminDashboard/challenges'));
const TrainerCohortView = lazyWithReload(() => import('./pages/adminDashboard/challengeCohort'));
const ClientExerciseHistory = lazyWithReload(() => import('./pages/adminDashboard/clientExerciseHistory'));
const Exercises = lazyWithReload(() => import('./pages/adminDashboard/exercises'));

const { Content } = Layout;

export default class IndexRoute extends Component {
  render() {
    return (
      <>
        <Switch>
          <Route path='/achievement-modal' exact component={AchievementModal} />
          <Route path='/' exact component={LandingPage} />

          <Route
            path='/userVerification/:regToken'
            exact
            component={Verification}
          />
          <Route
            path='/trainer-setup/:token'
            exact
            component={TrainerProfileSetup}
          />
          <Route
            path='/client-setup/:token'
            exact
            component={ClientSetup}
          />
          <Route path='/verify-email' exact component={VerifyEmailMessage} />
          <Route path='/quick-setup' exact component={QuickSetup} />
          {/* Internal showcase for shared UI primitives (Phase 1 design system).
              Public route — no PII, just visual examples. Bookmark while
              migrating pages. */}
          <Route path='/dev/components' exact component={ComponentsShowcase} />
          <Route path='/login' exact render={(props) => <Login {...props} />} />
          <Route
            path='/assessment'
            exact
            render={(props) => <ShortAssessment {...props} />}
          />
          <Route
            path='/assessment-completed'
            exact
            render={(props) =>
              props.location?.state?.fromAssessment
                ? <AssessmentCompleted {...props} />
                : <Redirect to='/dashboard/home' />
            }
          />
          <Route
            path='/trainer-welcome'
            exact
            render={(props) =>
              new URLSearchParams(props.location.search).get('welcome') === '1'
                ? <TrainerWelcome {...props} />
                : <Redirect to='/dashboard/admin' />
            }
          />
          <Route path='/unauthorized' exact component={Unauthorized} />
          <Route
            exact
            path='/share/my-workout/:token'
            component={SharedWorkout}
          />
          {/* Public, unauthenticated challenge landing page — full-bleed, auto-
              branded on the trainer's subdomain. No PrivateRoute. */}
          <Route
            exact
            path='/challenge/:slug'
            component={PublicChallengePage}
          />
          <Route path='/dashboard'>
            <Layout className='site-layout'>
              <Content className='dashboard-content'>
                <TitleSection {...this.props} />
                <ConditionalChatWidget />
                {/* Suspense for the lazy-loaded dashboard pages. Sits
                    below the chrome (TitleSection / ChatWidget / footer)
                    so they stay mounted while a page chunk loads. */}
                <Suspense
                  fallback={
                    <div style={{ padding: 40, textAlign: 'center' }}>
                      Loading…
                    </div>
                  }
                >
                  <Switch>
                  <PrivateRoute path='/dashboard/home' component={Home} />
                  {/* Canny feedback boards — trainer-facing (like the changelog),
                      so requireRole gates clients out even by direct URL. NOT
                      `exact`: Canny syncs its internal URLs under basePath
                      (/dashboard/feedback/*), so sub-paths must resolve here. */}
                  <PrivateRoute
                    path='/dashboard/feedback'
                    component={Feedback}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    path='/dashboard/tools/macro-calculator/manual'
                    component={MacrosManual}
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/tools/macro-calculator'
                    component={Profile}
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/tools/one-rep-max'
                    component={OneRepMaxTool}
                  />
                  <PrivateRoute
                    path='/dashboard/tools/carb-cycling'
                    component={CarbCyclingTool}
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/tools/macro-history'
                    component={MacroHistory}
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/tools/compliance'
                    component={FoodLogHistory}
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/tools/food-compliance'
                    component={FoodCompliance}
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/coach/meal-plan'
                    component={SuggesticMealPlan}
                    exact
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/coach/food-preferences'
                    component={NutritionRestrictions}
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/coach/shopping-list'
                    component={SuggesticShopping}
                    requireModule='nutrition'
                  />
                  {/* Strangler: V2 nutrition page lives at the bare path,
                      legacy custom meal plan sits at `/legacy` so trainers
                      and power users can still reach the weekly editor
                      while the V2 surface is rolled out. */}
                  <PrivateRoute
                    path='/dashboard/nutrition/meal-plan/legacy'
                    component={MealPlan}
                    exact
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/nutrition/meal-plan'
                    component={NutritionV2}
                    exact
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/workout/workout-program'
                    component={UserWorkoutPrograms}
                    exact
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/workout/workouts/:workoutId?'
                    component={UserWorkouts}
                  />
                  <PrivateRoute
                    path='/dashboard/workout/workout-plans'
                    component={UserWorkoutPlans}
                    exact
                  />
                  {/* Single-plan detail (cover-card list taps in here). Same
                      container in single-plan mode via the :planId param. */}
                  <PrivateRoute
                    path='/dashboard/workout/plan/:planId'
                    component={UserWorkoutPlans}
                    exact
                  />
                  <PrivateRoute
                    path='/dashboard/check-ins'
                    component={ClientCheckInsHistory}
                    exact
                  />
                  <PrivateRoute
                    path='/dashboard/your-week'
                    component={YourWeek}
                    exact
                  />
                  <PrivateRoute
                    path='/dashboard/nutrition/food-preferences'
                    component={CustomFoodPreferences}
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/nutrition/shopping-list'
                    component={CustomShopping}
                    requireModule='nutrition'
                  />
                  <PrivateRoute
                    path='/dashboard/stats/progress'
                    component={Stats}
                    exact
                  />
                  {/* Client Challenges tab — everything lives under
                      /dashboard/challenges (no requireRole so clients, incl.
                      challengeOnly, pass). Order: most specific first. */}
                  <PrivateRoute
                    exact
                    path='/dashboard/challenges/:cohortId/workouts/:planId'
                    component={UserWorkoutPlans}
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/challenges/:cohortId'
                    component={ChallengeDetail}
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/challenges'
                    component={ChallengesList}
                  />
                  <PrivateRoute
                    path='/dashboard/stats/exercise-history/:exerciseId'
                    component={UserExerciseDetail}
                    exact
                  />
                  <PrivateRoute
                    path='/dashboard/stats/exercise-history'
                    component={UserExerciseHistory}
                    exact
                  />
                  <PrivateRoute
                    path='/dashboard/nutrition/recipes'
                    component={Recipes}
                    exact
                    requireModule='nutrition'
                  />
                  {/* <PrivateRoute
                    path="/dashboard/nutrition/shopping-list"
                    component={Shopping}
                  /> */}
                  <PrivateRoute
                    path='/dashboard/messages'
                    component={Messages}
                    exact
                  />
                  <PrivateRoute
                    path='/dashboard/settings/:tab?'
                    component={Settings}
                  />
                  <PrivateRoute
                    path='/dashboard/profile'
                    component={AssessmentProfile}
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin'
                    component={AdminDashboard}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/clients'
                    component={AdminClients}
                    requireRole='trainer'
                  />
                  {/* Trainer roster + invites. The only admin route gated
                      above `trainer` — a plain trainer manages clients, not
                      other trainers, so requireRole is `organization_admin`
                      (hasMinimumRole is hierarchical, so platform_admin
                      passes too). The API's GET /admin/trainers enforces the
                      same floor; this just avoids rendering a page that
                      would only 403. */}
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/trainers'
                    component={AdminTrainers}
                    requireRole='organization_admin'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/clients/:id/food-log'
                    component={AdminFoodLog}
                    requireRole='trainer'
                  />
                  {/* Trainer-side mirror of /dashboard/tools/food-
                      compliance — reads the in-app UserFoodLog (the
                      FatSecret-powered one), not Fitbit. The legacy
                      /food-log route above still serves the Fitbit
                      data view until that ingestion is sunset. */}
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/clients/:id/food-compliance'
                    component={ClientFoodCompliance}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/clients/:userId/macros-manual'
                    component={MacrosManual}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/clients/:id/meal-plan'
                    component={AdminCreateMealPlan}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/clients/:id/edit-meal-plan'
                    component={MealPlan}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/clients/:id/exercise-history'
                    component={ClientExerciseHistory}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/meal-plan'
                    component={AdminMealPlanLibrary}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/meal-plan/:id'
                    component={AdminMealPlanTemplate}
                    requireRole='trainer'
                  />
                  {/* V2 meal-plan template editor (Phase 1+ of the
                      builder rewrite). New URL while we iterate;
                      will eventually replace the routes above and
                      the legacy editor moves to /meal-plan-legacy.
                      Two routes for create vs edit, same pattern as
                      the legacy editor's structure. */}
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/meal-plan-v2'
                    component={AdminMealPlanTemplateV2}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/meal-plan-v2/:id'
                    component={AdminMealPlanTemplateV2}
                    requireRole='trainer'
                  />
                  {/* Per-client V2 meal plan editor. Same builder UI as the
                      template editor above, but loads + saves the client's
                      CustomMealPlan instead of a reusable library template.
                      AssignModal redirects here with ?templateId=... so the
                      trainer can review a freshly-scaled plan before saving. */}
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/clients/:clientId/meal-plan-v2'
                    component={AdminMealPlanClientV2}
                    requireRole='trainer'
                  />
                  {/* Standalone client program editor (Phase 1 of the
                      program-builder unification — PROGRAM_BUILDER_PLAN.md).
                      MUST stay above the non-exact `/clients/:id` catch-all
                      below or the Switch swallows it. */}
                  {/* Optional :programId — present edits one of the client's
                      stacked programs; absent shows the program list. Both
                      handled by AdminProgramClientV2. */}
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/clients/:clientId/program-editor/:programId?'
                    component={AdminProgramClientV2}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/workout'
                    component={AdminWorkoutLibrary}
                    requireRole='trainer'
                  />
                  {/* Single non-exact mount for the trainer client surface.
                      The component owns an internal `<Switch>` that picks
                      between the V2 page (bare `/clients/:id`) and the
                      legacy `/profile/<section>` editors. */}
                  <PrivateRoute
                    path='/dashboard/admin/clients/:id'
                    component={AdminClientProfile}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/workouts'
                    component={AdminWorkoutLibrary}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/exercises'
                    component={Exercises}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/workout-program'
                    component={AdminWorkoutProgramLibrary}
                    requireRole='trainer'
                  />
                  {/* Standalone library program-TEMPLATE editor. `new`
                      is the create sentinel (programTemplateV2 maps it to
                      templateId=null). Renders <ProgramBuilder
                      mode="template"> — the template half of the
                      program-builder unification (PROGRAM_BUILDER_PLAN.md).
                      Exact, so it never shadows the grid route above. */}
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/workout-program/:programId'
                    component={AdminProgramTemplateV2}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/workout-plans'
                    component={AdminWorkoutPlanLibrary}
                    requireRole='trainer'
                  />
                  {/* Plan editor. REQUIRED :planId ('new' = create sentinel)
                      so it can't shadow the grid route above; plural base
                      keeps the "Workout Plans" nav item highlighted. */}
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/library/workout-plans/:planId'
                    component={AdminWorkoutPlanEditor}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/assessments'
                    component={AdminAssessmentsPage}
                    requireRole='trainer'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/challenges/:challengeId/landing'
                    component={ChallengeLandingBuilder}
                    requireRole='platform_admin'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/challenges/:cohortId'
                    component={TrainerCohortView}
                    requireRole='platform_admin'
                  />
                  <PrivateRoute
                    exact
                    path='/dashboard/admin/challenges'
                    component={AdminChallenges}
                    requireRole='platform_admin'
                  />
                  <Route component={NotFound} />
                  </Switch>
                </Suspense>
              </Content>
              <ActionFooter />
            </Layout>
          </Route>
          <Route component={NotFound} />
        </Switch>
      </>
    );
  }
}
